import { action, computed, makeObservable, observable } from 'mobx'

import { IOperationRule } from '~/client/graph'
import OperationRule from '~/client/src/shared/models/OperationRule'
import {
  DELETE_MANY_OPERATION_RULES,
  SAVE_MANY_OPERATION_RULES,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import Guard from '~/client/src/shared/utils/Guard'
import LiveListSyncGuard from '~/client/src/shared/utils/LiveListSyncGuard'

export default class OperationRulesStore {
  @observable public isDataReceived: boolean = false

  private readonly syncGuard = new LiveListSyncGuard()

  public constructor(private eventsStore: EventsStore) {
    makeObservable(this)
    Guard.requireAll({
      eventsStore,
    })
  }

  @computed
  public get list(): OperationRule[] {
    return Array.from(this.byId.values()).sort(
      (r1, r2) => r2.createdAt - r1.createdAt,
    )
  }

  @action.bound
  public receiveList(dtos: IOperationRule[]) {
    this.clearList()

    dtos.forEach((dto: IOperationRule) =>
      this.byId.set(dto.id, OperationRule.fromDto(dto)),
    )

    this.isDataReceived = true
  }

  /** Call before re-reading the list; pass the token to syncList. */
  public beginSync = (): number => {
    return this.syncGuard.beginSync()
  }

  /**
   * Bring the list up to date with a re-read without emptying it first.
   * Rules changed since the re-read began keep their newer state, unchanged
   * rules keep their instance, and a rule still being created (NEW_ID) stays.
   */
  @action.bound
  public syncList(dtos: IOperationRule[], syncToken: number) {
    const { syncGuard } = this
    if (!syncGuard.tryApply(syncToken)) {
      return
    }

    const freshIds = new Set(dtos.map(dto => dto.id))

    Array.from(this.byId.keys()).forEach(id => {
      const isKept =
        id === OperationRule.NEW_ID ||
        freshIds.has(id) ||
        syncGuard.isChangedSince(id, syncToken)

      if (!isKept) {
        this.byId.delete(id)
      }
    })

    dtos.forEach(dto => {
      if (syncGuard.isChangedSince(dto.id, syncToken)) {
        return
      }

      const current = this.byId.get(dto.id)
      const isChanged =
        !current ||
        current.updatedAt !== dto.updatedAt ||
        current.expression !== dto.expression ||
        current.assignExpression !== dto.assignExpression

      if (isChanged) {
        this.byId.set(dto.id, OperationRule.fromDto(dto))
      }
    })

    this.isDataReceived = true
  }

  @action.bound
  public receiveOne(id: string, dto: IOperationRule) {
    this.syncGuard.markChanged(dto?.id || id)

    if (dto) {
      this.removeRuleWithNewId()
      this.byId.set(dto.id, OperationRule.fromDto(dto))
    } else {
      this.byId.delete(id)
    }
  }

  /** Takes in rules the server wrote as part of a workflow type save, which returns no rules. */
  @action.bound
  public receiveSavedWithType(
    savedRules: OperationRule[],
    deletedRuleIds: string[],
  ) {
    savedRules.forEach(rule => this.byId.set(rule.id, rule))
    deletedRuleIds.forEach(id => this.byId.delete(id))
  }

  @action.bound
  public saveMany(
    operationRules: OperationRule[],
    callbackFn?: (rules: IOperationRule[]) => void,
  ) {
    if (!operationRules?.length) {
      return
    }

    if (operationRules.length === 1 && !operationRules[0].isExisting) {
      this.byId.set(OperationRule.NEW_ID, operationRules[0])
    }

    const dtos = operationRules.map(r => OperationRule.toDto(r))

    this.eventsStore.dispatch(SAVE_MANY_OPERATION_RULES, dtos, callbackFn)
  }

  @action.bound
  public removeMany(ruleIds: string[], callbackFn?: () => void) {
    if (!ruleIds?.length) {
      return
    }

    this.eventsStore.dispatch(DELETE_MANY_OPERATION_RULES, ruleIds, callbackFn)
  }

  @action.bound
  public clearList() {
    this.byId.clear()
    this.syncGuard.reset()
    this.isDataReceived = false
  }

  @action.bound
  public removeRuleWithNewId() {
    this.byId.delete(OperationRule.NEW_ID)
  }

  public isRuleExisting = (id: string): boolean => {
    return this.byId.has(id)
  }

  public getRuleById = (id: string): OperationRule => {
    return this.isRuleExisting(id) && this.byId.get(id)
  }

  private get byId() {
    return this.eventsStore.appState.operationRules
  }
}
